import { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL_NODE } from '../Constants/Config';

/**
 * Container deposit (CRV / bottle deposit) for online ordering.
 *
 * The store's setup comes from Node (POST deposit/public/config) and is kept in
 * a module cache, so the pure pricing code (tax, grand total, order payload)
 * reads exactly the numbers the page shows. No setup, feature off, or a failed
 * fetch => no deposit anywhere, and checkout behaves exactly as before.
 *
 * Money rules (same as the POS): deposit line = qty x per_item_amount, never
 * discounted; when tax_mode is "inherit" it is added to that line's taxable base,
 * so it picks up exactly the item's own taxes; it is added to the grand total.
 */

const cache = new Map();
const inflight = new Map();
let active = null;

const norm = (id) => String(id ?? '').trim().toUpperCase();
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function indexItems(items) {
  const byKey = new Map();
  (items || []).forEach((i) => byKey.set(`${Number(i.product_id)}:${Number(i.variant_id) || 0}`, i));
  return byKey;
}

export function fetchDepositConfig(merchantId) {
  const key = norm(merchantId);
  if (!key) return Promise.resolve(null);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const request = axios
    .post(`${BASE_URL_NODE}deposit/public/config`, { merchant_id: merchantId }, { timeout: 8000 })
    .then((res) => {
      const r = res?.data?.result;
      const cfg = r?.enabled && Array.isArray(r.items) && r.items.length
        ? { merchantId: key, feeName: r.fee_name, taxMode: r.tax_mode, byKey: indexItems(r.items) }
        : null;
      cache.set(key, cfg);
      return cfg;
    })
    .catch(() => null)
    .finally(() => inflight.delete(key));
  inflight.set(key, request);
  return request;
}

export function setActiveDepositConfig(cfg) {
  active = cfg || null;
}

export function getActiveDepositConfig() {
  return active;
}

/** Deposit on one cart line: { perItem, amount, taxable, feeName } or null. */
export function depositForCartItem(item, cfg = active) {
  if (!cfg || !item) return null;
  const pid = Number(item.productId ?? item.product?.id ?? item.product_id) || 0;
  const vid = Number(item.variantId ?? item.variant_id) || 0;
  const qty = Number(item.selectedQuantity ?? item.qty) || 0;
  if (!pid || qty <= 0) return null;
  const hit = (vid && cfg.byKey.get(`${pid}:${vid}`)) || cfg.byKey.get(`${pid}:0`);
  const perItem = Number(hit?.per_item_amount) || 0;
  if (perItem <= 0) return null;
  return {
    perItem,
    amount: round2(qty * perItem),
    taxable: cfg.taxMode !== 'none',
    feeName: cfg.feeName,
  };
}

/**
 * Deposit for a DISPLAYED cart row. Rows can merge several cart entries of the same
 * product; they show `aggregatedQuantity`, so the fee must follow that same number.
 * Pricing (tax, totals, payload) keeps using the per-entry quantity above.
 */
export function depositForDisplayRow(row, cfg = active) {
  if (!row) return null;
  return depositForCartItem(
    { ...row, selectedQuantity: row.aggregatedQuantity || row.selectedQuantity },
    cfg,
  );
}

/** Taxable deposit to add to a line's tax base (0 when none or not taxed). */
export function taxableDepositForCartItem(item, cfg = active) {
  const dep = depositForCartItem(item, cfg);
  return dep && dep.taxable ? dep.amount : 0;
}

export function cartDepositTotal(items, cfg = active) {
  return round2((items || []).reduce((sum, i) => sum + (depositForCartItem(i, cfg)?.amount || 0), 0));
}

export function depositFeeName(cfg = active) {
  return cfg?.feeName || 'Bottle Deposit';
}

/** React: load the store's deposit setup; the component re-renders when it lands. */
export function useContainerDeposit(merchantId) {
  const [cfg, setCfg] = useState(() => cache.get(norm(merchantId)) ?? null);
  useEffect(() => {
    let alive = true;
    const cached = cache.get(norm(merchantId));
    if (cached !== undefined) {
      setActiveDepositConfig(cached);
      setCfg(cached);
    }
    fetchDepositConfig(merchantId).then((c) => {
      if (!alive) return;
      setActiveDepositConfig(c);
      setCfg(c);
    });
    return () => {
      alive = false;
    };
  }, [merchantId]);
  return cfg;
}
